import { useEffect, useState, useMemo } from 'react'
import { X, Loader2, Zap, MapPin, Navigation } from 'lucide-react'
import type { ChargepointSummary, SpeedTier } from '@fuelfinder/shared'
import { calculateDistanceMiles } from '@fuelfinder/shared'
import { fetchNationalDataset } from '../lib/api'
import { loadCachedLocation } from '../hooks/useLocation'

interface StatsModalProps {
  open: boolean
  onClose: () => void
  /** Centre the map on a coordinate (used by the "take me there" tile) */
  onNavigateTo: (lat: number, lng: number) => void
}

/** Radius (miles) for the "near you" stat */
const NEAR_RADIUS_MI = 5

/** Speed-tier colours (match the map markers) */
const TIER_COLOR: Record<SpeedTier, string> = {
  rapid: '#5840ff', // brand-500
  fast: '#8b85f9', // brand-400
  slow: '#f59e0b', // amber-500
}
const TIER_LABEL: Record<SpeedTier, string> = {
  rapid: 'Rapid (50kW+)',
  fast: 'Fast (7–50kW)',
  slow: 'Slow (≤7kW)',
}

/**
 * Merge operator name variants ("BP Pulse (UK)" + "bp pulse", etc.) into a
 * single short canonical name for the network leaderboard.
 */
function normalizeOperator(raw: string): string {
  const key = raw.trim().toLowerCase()
  const aliases: Record<string, string> = {
    'bp pulse': 'BP Pulse',
    'bp pulse (uk)': 'BP Pulse',
    'pod point': 'Pod Point',
    'pod point (uk)': 'Pod Point',
    'shell recharge solutions (uk)': 'Shell Recharge',
    'shell recharge': 'Shell Recharge',
    'char.gy': 'char.gy',
    'chargeplace scotland': 'ChargePlace Scotland',
    'instavolt ltd': 'InstaVolt',
    'instavolt': 'InstaVolt',
    'total energies (uk)': 'TotalEnergies',
    'totalenergies': 'TotalEnergies',
    'osprey charging network': 'Osprey',
    'osprey': 'Osprey',
    'surecharge (fm conway) (uk)': 'SureCharge',
    'ubitricity': 'ubitricity',
  }
  if (aliases[key]) return aliases[key]
  if (key.includes('unknown')) return 'Independent'
  return raw.replace(/\(uk\)/gi, '').replace(/\bltd\b/gi, '').trim()
}

interface Stats {
  total: number
  connectors: number
  tiers: Record<SpeedTier, number>
  ultraRapid: number
  fastest: ChargepointSummary | null
  networks: Array<{ name: string; count: number }>
  near: { count: number; rapid: number } | null
}

function computeStats(cps: ChargepointSummary[]): Stats {
  const tiers: Record<SpeedTier, number> = { slow: 0, fast: 0, rapid: 0 }
  const opCounts = new Map<string, number>()
  let connectors = 0
  let ultraRapid = 0
  let fastest: ChargepointSummary | null = null

  for (const cp of cps) {
    tiers[cp.speedTier]++
    connectors += cp.totalConnectors || 0
    if (cp.maxPowerKW >= 150) ultraRapid++
    if (!fastest || cp.maxPowerKW > fastest.maxPowerKW) fastest = cp
    const op = normalizeOperator(cp.operator)
    opCounts.set(op, (opCounts.get(op) ?? 0) + 1)
  }

  const networks = [...opCounts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)

  const loc = loadCachedLocation()
  let near: Stats['near'] = null
  if (loc) {
    let count = 0
    let rapid = 0
    for (const cp of cps) {
      if (calculateDistanceMiles(loc.lat, loc.lng, cp.lat, cp.lng) <= NEAR_RADIUS_MI) {
        count++
        if (cp.speedTier === 'rapid') rapid++
      }
    }
    near = { count, rapid }
  }

  return { total: cps.length, connectors, tiers, ultraRapid, fastest, networks, near }
}

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-center">
      <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{value}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{label}</p>
    </div>
  )
}

export function StatsModal({ open, onClose, onNavigateTo }: StatsModalProps) {
  const [cps, setCps] = useState<ChargepointSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchNationalDataset()
      .then((data) => { if (!cancelled) setCps(data) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load stats') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open])

  const stats = useMemo(() => (cps ? computeStats(cps) : null), [cps])

  if (!open) return null

  const tierOrder: SpeedTier[] = ['rapid', 'fast', 'slow']

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md max-h-[85dvh] overflow-y-auto">
        {/* Header (pinned so the close button stays reachable while scrolling) */}
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">UK Charging Stats</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 min-h-[44px] rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close stats"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 size={28} className="text-brand-500 animate-spin" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading UK charging data…</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {stats && (
            <>
              {/* National totals */}
              <div className="grid grid-cols-2 gap-3">
                <StatTile value={stats.total.toLocaleString()} label="Charge points" />
                <StatTile value={stats.connectors.toLocaleString()} label="Connectors" />
                <StatTile value={stats.tiers.rapid.toLocaleString()} label="Rapid (50kW+)" />
                <StatTile value={stats.ultraRapid.toLocaleString()} label="Ultra-rapid (150kW+)" />
              </div>

              {/* Near you */}
              {stats.near && (
                <div className="rounded-lg border-2 border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/20 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Navigation size={15} className="text-brand-600 dark:text-brand-400" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-400">
                      Near you
                    </span>
                  </div>
                  <p className="text-slate-900 dark:text-white">
                    <span className="text-2xl font-bold tabular-nums">{stats.near.count.toLocaleString()}</span>{' '}
                    charge points within {NEAR_RADIUS_MI} miles
                    {stats.near.count > 0 && (
                      <span className="text-sm text-slate-500 dark:text-slate-400"> · {stats.near.rapid} rapid</span>
                    )}
                  </p>
                </div>
              )}

              {/* Fastest charger */}
              {stats.fastest && (
                <button
                  onClick={() => { onNavigateTo(stats.fastest!.lat, stats.fastest!.lng); onClose() }}
                  className="w-full text-left rounded-lg border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Zap size={15} className="text-amber-600" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                      Fastest in the UK
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white truncate">{stats.fastest.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                        <MapPin size={11} />
                        {stats.fastest.town || stats.fastest.postcode || 'Tap to view'}
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-amber-600 shrink-0">{stats.fastest.maxPowerKW}kW</span>
                  </div>
                </button>
              )}

              {/* Speed mix */}
              <section>
                <h3 className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-2">
                  Speed mix
                </h3>
                <div className="flex h-4 rounded-full overflow-hidden">
                  {tierOrder.map((t) => {
                    const pct = stats.total > 0 ? (stats.tiers[t] / stats.total) * 100 : 0
                    return pct > 0 ? (
                      <div key={t} style={{ width: `${pct}%`, backgroundColor: TIER_COLOR[t] }} title={`${TIER_LABEL[t]}: ${stats.tiers[t].toLocaleString()}`} />
                    ) : null
                  })}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {tierOrder.map((t) => (
                    <div key={t} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: TIER_COLOR[t] }} />
                      {TIER_LABEL[t]} · <span className="tabular-nums">{stats.tiers[t].toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Network leaderboard */}
              {stats.networks.length > 0 && (
                <section>
                  <h3 className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-3">
                    Biggest networks
                  </h3>
                  <div className="space-y-1.5">
                    {stats.networks.map((net) => {
                      const barWidth = 10 + (net.count / stats.networks[0].count) * 90
                      return (
                        <div key={net.name} className="flex items-center gap-2">
                          <span className="text-xs text-slate-700 dark:text-slate-300 w-28 truncate shrink-0 text-right" title={net.name}>
                            {net.name}
                          </span>
                          <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                            <div className="h-full rounded bg-brand-500 transition-all duration-500" style={{ width: `${barWidth}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 w-12 text-right tabular-nums">
                            {net.count.toLocaleString()}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </section>
              )}

              <p className="text-[11px] text-slate-400 dark:text-slate-500">
                Based on {stats.total.toLocaleString()} UK charge points. Data from OpenChargeMap and operator networks.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-900 p-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="w-full py-3 px-4 rounded-lg text-white bg-brand-600 hover:bg-brand-700 font-medium transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
